import React from 'react'
import { Clock, MapPin, Users, ExternalLink } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'

interface EventCardProps {
  event: CalendarEvent
  onClick?: () => void
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick }) => {
  const formatTime = (dateString: string) => {
    try {
      const date = parseISO(dateString)
      return format(date, 'HH:mm', { locale: ptBR })
    } catch {
      return ''
    }
  }

  const startTime = formatTime(event.start)
  const endTime = formatTime(event.end)

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white hover:shadow-md border border-border rounded-2xl p-4 shadow-sm transition-all duration-200 dark:bg-neutral-800/50 dark:hover:bg-neutral-800 dark:border-neutral-700 dark:rounded-lg dark:shadow-none dark:hover:shadow-none"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#0F172A] truncate dark:text-neutral-50 dark:font-medium">
            {event.title || 'Sem titulo'}
          </h3>

          <div className="mt-2 space-y-1.5">
            {(startTime || endTime) && (
              <div className="flex items-center gap-2 text-sm text-[#64748B] dark:text-neutral-400">
                <Clock className="w-4 h-4 flex-shrink-0 text-[#2563EB] dark:text-[#60A5FA]" />
                <span>
                  {startTime}
                  {endTime && ` - ${endTime}`}
                </span>
              </div>
            )}

            {event.location && (
              <div className="flex items-center gap-2 text-sm text-[#64748B] dark:text-neutral-400">
                <MapPin className="w-4 h-4 flex-shrink-0 text-[#2563EB] dark:text-[#60A5FA]" />
                <span className="truncate">{event.location}</span>
              </div>
            )}

            {event.attendees && event.attendees.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-[#64748B] dark:text-neutral-400">
                <Users className="w-4 h-4 flex-shrink-0 text-[#2563EB] dark:text-[#60A5FA]" />
                <span>{event.attendees.length} participante(s)</span>
              </div>
            )}
          </div>
        </div>

        {event.html_link && (
          <a
            href={event.html_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="p-2 hover:bg-[#F1F5F9] rounded-xl transition-colors duration-200 dark:hover:bg-neutral-700 dark:rounded-lg"
          >
            <ExternalLink className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" />
          </a>
        )}
      </div>
    </button>
  )
}

export default EventCard
